import { ChevronDown, ChevronRight, Loader2, Pencil, X } from "lucide-react";
import type { PreviewSeek } from "../TrimExtendPanel";
import { iconBtnStyle } from "../styles";
import { CutExtendGroup } from "./CutExtendGroup";
import { EditClipPanel, type EditClipSectionId } from "./EditClipPanel";
import { isTerminalStatus, type JobView } from "./jobView";
import { RenderHistoryPanel } from "./RenderHistoryPanel";

/**
 * "Edit this clip" — the Studio's one collapsed bar for everything that
 * changes the clip itself rather than its design (spec 2026-09-16 §2.4).
 *
 * Two pieces, mounted in two places: the BAR (collapsed line + the six
 * group buttons) lives in the Studio's measured chrome, and the open group's
 * body is a DRAWER the Studio lays over the top of the stage — so opening a
 * group never shrinks the preview and never scrolls the page (review round 1,
 * item 3). The drawer caps its own height and scrolls inside itself.
 *
 * The groups are the video page's old Edit-clip sections plus Cut & extend
 * and the render history — mounted verbatim; the bar only names them.
 * Actions that start a job report it through `onJobStarted(group)` and the
 * Studio follows the job exactly like Approve & render. While anything is
 * building — a followed job, or a job the server is running for this clip —
 * every group is locked (item 5).
 */
export type EditGroupId = EditClipSectionId | "startend" | "versions";

export const EDIT_GROUPS: ReadonlyArray<{ id: EditGroupId; label: string; hint: string }> = [
  { id: "captions", label: "Captions & text", hint: "timing, captions, the post caption" },
  { id: "startend", label: "Start & end", hint: "trim or extend, fix the name" },
  { id: "sound", label: "Sound", hint: "music level or track — no rebuild" },
  { id: "camera", label: "Camera & framing", hint: "aim the crop, re-judge the shots" },
  { id: "recut", label: "Re-cut from scratch", hint: "a fresh cut from the source" },
  { id: "versions", label: "Versions", hint: "earlier renders, restore" },
];

/** What the done notice says once a job started from a group has landed. */
export const EDIT_GROUP_DONE: Record<EditGroupId, string> = {
  captions: "Captions updated — the new render is live. It's in the Library now; the previous version stays under Versions.",
  startend: "Start & end applied — the new render is live. It's in the Library now; the previous version stays under Versions.",
  sound: "Sound updated — the new mix is live. It's in the Library now; the previous version stays under Versions.",
  camera: "Camera re-framed — the new render is live. It's in the Library now; the previous version stays under Versions.",
  recut: "Re-cut finished — the new render is live. It's in the Library now; the previous version stays under Versions.",
  versions: "Version restored — the Studio now shows it. The one you left stays under Versions.",
};

/** The one lock rule, shared by the bar and the drawer. */
export function editLocked(view: JobView | null, rendering: boolean): boolean {
  return rendering || (view !== null && !isTerminalStatus(view.status));
}

const lockedNote = (
  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: "var(--ql-fg-muted)", padding: "6px 2px" }}>
    <Loader2 size={14} className="ql-spin" />
    Building the new version — the groups unlock when it lands.
  </div>
);

export interface EditClipBarProps {
  /** null while the job is still loading. */
  view: JobView | null;
  open: boolean;
  onToggle: () => void;
  group: EditGroupId | null;
  onGroup: (g: EditGroupId | null) => void;
  /** A followed job is in flight. */
  rendering: boolean;
  barRef?: React.Ref<HTMLDivElement>;
}

export function EditClipBar({ view, open, onToggle, group, onGroup, rendering, barRef }: EditClipBarProps): React.JSX.Element {
  const locked = editLocked(view, rendering);
  return (
    <div
      ref={barRef}
      style={{
        background: "rgba(16,18,27,.72)",
        border: "1px solid rgba(255,255,255,.06)",
        borderRadius: 18,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        data-tour="studio-edit-clip"
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 16px",
          background: "transparent",
          border: "none",
          color: open ? "var(--ql-yellow)" : "var(--ql-fg)",
          fontFamily: "inherit",
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <Pencil size={15} strokeWidth={1.75} />
        <span style={{ whiteSpace: "nowrap" }}>Edit this clip</span>
        <span style={{ fontSize: 12, color: "var(--ql-fg-muted)", fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          — captions, start &amp; end, sound, camera, a fresh cut, versions
        </span>
        <span style={{ marginLeft: "auto", color: "var(--ql-fg-muted)", display: "inline-flex" }}>
          {open ? <ChevronDown size={16} strokeWidth={2} /> : <ChevronRight size={16} strokeWidth={2} />}
        </span>
      </button>

      {open && (
        <div style={{ padding: "0 16px 14px" }}>
          <div role="tablist" aria-label="Edit this clip" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {EDIT_GROUPS.map((g) => {
              const isActive = group === g.id;
              return (
                <button
                  key={g.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onGroup(isActive ? null : g.id)}
                  disabled={locked}
                  title={locked ? "Locked while the new version is being built" : g.hint}
                  style={{
                    padding: "8px 13px",
                    borderRadius: 10,
                    cursor: locked ? "default" : "pointer",
                    fontFamily: "inherit",
                    fontSize: 12.5,
                    fontWeight: 700,
                    border: "1px solid " + (isActive ? "rgba(255,213,0,.85)" : "rgba(255,255,255,.08)"),
                    background: isActive ? "rgba(255,213,0,.12)" : "rgba(255,255,255,.03)",
                    color: isActive ? "var(--ql-yellow)" : "var(--ql-fg)",
                    opacity: locked ? 0.55 : 1,
                  }}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
          {locked && <div style={{ marginTop: 8 }}>{lockedNote}</div>}
        </div>
      )}
    </div>
  );
}

export interface EditClipDrawerProps {
  jobId: string;
  view: JobView | null;
  group: EditGroupId;
  onClose: () => void;
  seek: PreviewSeek;
  onJobStarted: (group: EditGroupId) => void;
  rendering: boolean;
  /** Bumped after a followed job lands: the group remounts with fresh state. */
  epoch: number;
}

/**
 * The open group's body. The Studio positions it absolutely over the top of
 * the stage container (full stage width); it never takes layout space.
 */
export function EditClipDrawer({ jobId, view, group, onClose, seek, onJobStarted, rendering, epoch }: EditClipDrawerProps): React.JSX.Element {
  const locked = editLocked(view, rendering);
  const label = EDIT_GROUPS.find((g) => g.id === group)?.label ?? "Edit this clip";
  return (
    <div
      role="tabpanel"
      aria-label={label}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        maxHeight: "min(60vh, 520px)",
        overflowY: "auto",
        boxSizing: "border-box",
        padding: "12px 16px 16px",
        borderRadius: 16,
        background: "rgba(12,14,20,.97)",
        border: "1px solid rgba(255,213,0,.22)",
        boxShadow: "0 18px 48px rgba(0,0,0,.55)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ql-yellow)", textTransform: "uppercase", letterSpacing: ".08em" }}>
          {label}
        </span>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          aria-label={`Close ${label}`}
          style={{ ...iconBtnStyle, width: 28, height: 28, marginLeft: "auto" }}
        >
          <X size={13} />
        </button>
      </div>
      <div key={`${group}-${epoch}`}>
        {locked ? (
          lockedNote
        ) : !view ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: "var(--ql-fg-muted)", padding: "6px 2px" }}>
            <Loader2 size={14} className="ql-spin" />
            Loading the clip…
          </div>
        ) : group === "startend" ? (
          <CutExtendGroup view={view} seek={seek} onJobStarted={() => onJobStarted("startend")} />
        ) : group === "versions" ? (
          <RenderHistoryPanel
            jobId={jobId}
            status={view.status}
            onRestored={() => onJobStarted("versions")}
            empty="No earlier versions yet — every rebuild keeps the version it replaces here, ready to restore."
          />
        ) : (
          <EditClipPanel
            jobId={jobId}
            status={view.status}
            hasVideo={view.hasVideo}
            hasTranscript={view.hasTranscript}
            originalFrom={view.input?.from}
            originalTo={view.input?.to}
            originalDuration={view.input?.duration}
            originalPhrase={view.input?.startPhrase}
            originalEndPhrase={view.input?.endPhrase}
            originalMusicTrack={view.input?.musicTrack}
            only={group}
            onJobStarted={() => onJobStarted(group)}
          />
        )}
      </div>
    </div>
  );
}
